import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'

/**
 * Determines if a URL is internal (same domain/relative) or external
 */
const isInternalLink = (url: string): boolean => {
  if (!url) return false

  // Relative URLs (starting with /, #, or ?)
  if (url.startsWith('/') || url.startsWith('#') || url.startsWith('?')) {
    return true
  }

  // Check if it's the same domain
  try {
    const linkUrl = new URL(url, window.location.origin)

    return linkUrl.origin === window.location.origin
  } catch {
    // If URL parsing fails, treat as internal for safety
    return true
  }
}

const CustomLinkPlugin = () => {
  const [editor] = useLexicalComposerContext()
  const history = useHistory()

  useEffect(() => {
    return editor.registerRootListener((rootElement) => {
      if (!rootElement) return null

      const handleClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement
        const linkElement = target.closest('a')

        if (linkElement) {
          const href = linkElement.getAttribute('href')

          if (href && isInternalLink(href)) {
            // Only handle internal links - prevent default and use React Router
            event.preventDefault()
            event.stopPropagation()

            if (href.startsWith('#') || href.startsWith('?')) {
              // For hash or query links, update current location
              window.location.hash = href.startsWith('#') ? href : ''
              if (href.startsWith('?')) {
                history.push({ search: href })
              }
            } else {
              // Navigate using React Router for path changes
              history.push(href)
            }

            return false
          }

          // External links: do nothing, let them behave normally
        }

        return undefined
      }

      // Use capture phase to ensure we catch the event before it bubbles
      rootElement.addEventListener('click', handleClick, true)

      return () => {
        rootElement.removeEventListener('click', handleClick, true)
      }
    })
  }, [editor, history])

  return null
}

export default CustomLinkPlugin
